/**
 * Public showcase / portfolio page displaying completed projects.
 *
 * Fetches apps from the API and falls back to translated demo entries
 * if the backend is unavailable. Clicking a card opens a detail dialog
 * with a screenshot carousel and store links.
 *
 * @module ShowcasePage
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import {
  Box,
  Container,
  Typography,
  Grid2 as Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import AppleIcon from '@mui/icons-material/Apple';
import ShopIcon from '@mui/icons-material/Shop';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import api from '../api';
import { GREEN, DARK_BLUE } from '../theme';
import { PageTransition, FadeInSection, FloatingOrb } from '../components/AnimatedComponents';

/** Shape of a media item (image or video). */
interface ShowcaseMedia {
  url: string;
  type: 'image' | 'video';
  filename: string;
}

/** Shape of a translation for a single language. */
interface ShowcaseTranslation {
  name: string;
  description: string;
  short_description: string;
}

/** Shape of a showcase app as returned by the API. */
interface ShowcaseApp {
  id: string;
  name: string;
  description: string;
  short_description: string;
  translations?: Record<string, ShowcaseTranslation>;
  platforms: string[];
  app_store_url?: string;
  play_store_url?: string;
  website_url?: string;
  screenshots: string[];
  media: ShowcaseMedia[];
  tags: string[];
}

/** Resolve the best translation for a showcase app given a language code. */
function getTranslation(app: ShowcaseApp, lang: string): ShowcaseTranslation {
  const tr = app.translations;
  if (tr) {
    if (tr[lang]) return tr[lang];
    if (tr['nl']) return tr['nl'];
    if (tr['en']) return tr['en'];
  }
  return { name: app.name, description: app.description, short_description: app.short_description };
}

/** Portfolio grid with detail dialog and demo-app fallback. */
export default function ShowcasePage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'nl';
  const [apps, setApps] = useState<ShowcaseApp[]>([]);
  const [selectedApp, setSelectedApp] = useState<ShowcaseApp | null>(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const DEMO_APPS: ShowcaseApp[] = [];

  useEffect(() => {
    api
      .get('/api/showcase/')
      .then((res) => {
        setApps(res.data);
      })
      .catch(() => {
        setApps([]);
      });
  }, []);

  const handleOpenApp = (app: ShowcaseApp) => {
    setSelectedApp(app);
    setSlideIndex(0);
  };

  return (
    <PageTransition>
      <SEO
        title={t('showcase.seoTitle')}
        description={t('showcase.seoDesc')}
        canonical="/showcase"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${t('seo.siteName')} ${t('showcase.seoTitle')}`,
          description: t('showcase.seoDesc'),
          url: 'https://www.booge-it.com/showcase',
        }}
      />
      <Box
        sx={{
          py: { xs: 4, md: 6 },
          position: 'relative',
          overflow: 'hidden',
          minHeight: '80vh',
        }}
      >
        <FloatingOrb size={350} color={GREEN} top={-100} right={-100} />
        <FloatingOrb size={250} color={DARK_BLUE} bottom={-60} left={-80} animationDelay="5s" />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <FadeInSection>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2, px: 2.5, py: 0.75, borderRadius: 6, bgcolor: alpha(GREEN, 0.08), border: `1px solid ${alpha(GREEN, 0.15)}` }}>
                <AutoAwesomeIcon sx={{ fontSize: 18, color: GREEN }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: GREEN }}>{t('showcase.badge')}</Typography>
              </Box>
              <Typography variant="h2" sx={{ mb: 1, fontWeight: 700 }}>
                {t('showcase.title')}{' '}
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(135deg, ${GREEN}, #2d9c1c)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {t('showcase.titleHighlight')}
                </Box>
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ maxWidth: 600, mx: 'auto', fontSize: '1.05rem' }}
              >
                {t('showcase.desc')}
              </Typography>
            </Box>
          </FadeInSection>

          <Grid container spacing={3}>
            {apps.map((app, index) => {
              const tr = getTranslation(app, lang);
              return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={app.id}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  style={{ height: '100%' }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 40px ${alpha(DARK_BLUE, 0.12)}`,
                        '& .card-media-overlay': { opacity: 1 },
                        '& .card-letter': { transform: 'scale(1.2)', opacity: 0.5 },
                      },
                    }}
                    onClick={() => handleOpenApp(app)}
                  >
                    <CardMedia
                      sx={{
                        height: 200,
                        bgcolor: DARK_BLUE,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      {(() => {
                        const firstMedia = app.media && app.media.length > 0
                          ? app.media[0]
                          : app.screenshots.length > 0
                            ? { url: app.screenshots[0], type: 'image' as const, filename: '' }
                            : null;

                        if (firstMedia && firstMedia.type === 'video') {
                          return (
                            <Box sx={{ width: '100%', height: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(DARK_BLUE, 0.9) }}>
                              <PlayCircleOutlineIcon sx={{ fontSize: 64, color: GREEN, opacity: 0.8 }} />
                            </Box>
                          );
                        }
                        if (firstMedia) {
                          return (
                            <Box
                              component="img"
                              src={firstMedia.url}
                              alt={app.name}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          );
                        }
                        return (
                          <Typography
                            className="card-letter"
                            variant="h3"
                            sx={{
                              color: GREEN,
                              fontWeight: 800,
                              opacity: 0.3,
                              transition: 'all 0.4s ease',
                              fontSize: '4rem',
                            }}
                          >
                            {tr.name.charAt(0)}
                          </Typography>
                        );
                      })()}
                      <Box
                        className="card-media-overlay"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          background: `linear-gradient(180deg, transparent 30%, ${alpha(DARK_BLUE, 0.8)} 100%)`,
                          opacity: 0,
                          transition: 'opacity 0.35s ease',
                          display: 'flex',
                          alignItems: 'flex-end',
                          p: 2,
                        }}
                      >
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                          {t('showcase.clickExplore')}
                        </Typography>
                      </Box>
                    </CardMedia>
                    <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                        {tr.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                        {tr.short_description}
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {app.platforms.map((p) => (
                          <Chip key={p} label={p} size="small" variant="outlined" />
                        ))}
                      </Stack>
                      {app.tags.length > 0 && (
                        <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                          {app.tags.map((tag) => (
                            <Chip key={tag} label={tag} size="small" color="secondary" />
                          ))}
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
              );
            })}
          </Grid>
        </Container>

        {/* App Detail Dialog */}
        <Dialog
          open={!!selectedApp}
          onClose={() => setSelectedApp(null)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 4,
              overflow: 'hidden',
            },
          }}
        >
          {selectedApp && (() => {
            const selectedTr = getTranslation(selectedApp, lang);
            return (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <DialogTitle
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: `linear-gradient(135deg, ${alpha(DARK_BLUE, 0.03)}, ${alpha(GREEN, 0.03)})`,
                    borderBottom: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
                  }}
                >
                  <Typography variant="h4" component="span" sx={{ fontWeight: 700 }}>
                    {selectedTr.name}
                  </Typography>
                  <IconButton
                    onClick={() => setSelectedApp(null)}
                    sx={{
                      bgcolor: alpha(DARK_BLUE, 0.05),
                      '&:hover': { bgcolor: alpha(DARK_BLUE, 0.1) },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                  {/* Slideshow */}
                  {(() => {
                    const allMedia: ShowcaseMedia[] =
                      selectedApp.media && selectedApp.media.length > 0
                        ? selectedApp.media
                        : selectedApp.screenshots.map((url) => ({ url, type: 'image' as const, filename: '' }));
                    const currentMedia = allMedia[slideIndex];

                    if (allMedia.length > 0) {
                      return (
                        <Box
                          sx={{
                            position: 'relative',
                            mb: 3,
                            bgcolor: '#000',
                            borderRadius: 3,
                            overflow: 'hidden',
                            height: { xs: 200, md: 350 },
                          }}
                        >
                          {currentMedia?.type === 'video' ? (
                            <Box
                              component="video"
                              key={currentMedia.url}
                              src={currentMedia.url}
                              controls
                              autoPlay
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                              }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={currentMedia?.url}
                              alt={`${selectedTr.name} ${slideIndex + 1}`}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                              }}
                            />
                          )}
                          {allMedia.length > 1 && (
                            <>
                              <IconButton
                                onClick={() =>
                                  setSlideIndex((i) =>
                                    i > 0 ? i - 1 : allMedia.length - 1
                                  )
                                }
                                sx={{
                                  position: 'absolute',
                                  left: 8,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  bgcolor: 'rgba(255,255,255,0.9)',
                                  backdropFilter: 'blur(10px)',
                                  '&:hover': { bgcolor: 'white' },
                                }}
                              >
                                <ArrowBackIosNewIcon />
                              </IconButton>
                              <IconButton
                                onClick={() =>
                                  setSlideIndex((i) =>
                                    i < allMedia.length - 1 ? i + 1 : 0
                                  )
                                }
                                sx={{
                                  position: 'absolute',
                                  right: 8,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  bgcolor: 'rgba(255,255,255,0.9)',
                                  backdropFilter: 'blur(10px)',
                                  '&:hover': { bgcolor: 'white' },
                                }}
                              >
                                <ArrowForwardIosIcon />
                              </IconButton>
                            </>
                          )}
                          {/* Thumbnail dots */}
                          {allMedia.length > 1 && (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              sx={{
                                position: 'absolute',
                                bottom: 12,
                                left: '50%',
                                transform: 'translateX(-50%)',
                              }}
                            >
                              {allMedia.map((m, idx) => (
                                <Box
                                  key={m.url}
                                  onClick={() => setSlideIndex(idx)}
                                  sx={{
                                    width: m.type === 'video' ? 24 : 10,
                                    height: 10,
                                    borderRadius: 5,
                                    bgcolor: idx === slideIndex ? GREEN : 'rgba(255,255,255,0.5)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  {m.type === 'video' && (
                                    <PlayCircleOutlineIcon sx={{ fontSize: 8, color: idx === slideIndex ? 'white' : 'rgba(255,255,255,0.7)' }} />
                                  )}
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      );
                    }
                    return null;
                  })()}

                  {(!selectedApp.media || selectedApp.media.length === 0) && selectedApp.screenshots.length === 0 && (
                    <Box
                      sx={{
                        mb: 3,
                        background: `linear-gradient(135deg, ${DARK_BLUE}, ${alpha(DARK_BLUE, 0.85)})`,
                        borderRadius: 3,
                        height: { xs: 150, md: 250 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          background: `radial-gradient(circle at 30% 50%, ${alpha(GREEN, 0.15)} 0%, transparent 60%)`,
                        }}
                      />
                      <Typography variant="h1" sx={{ color: GREEN, fontWeight: 800, opacity: 0.2, fontSize: '8rem' }}>
                        {selectedTr.name.charAt(0)}
                      </Typography>
                    </Box>
                  )}

                  <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                    {selectedTr.description}
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                    {t('showcase.availableOn')}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
                    {selectedApp.platforms.map((p) => (
                      <Chip key={p} label={p} color="primary" />
                    ))}
                  </Stack>

                  {/* Store Links */}
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    {selectedApp.play_store_url && (
                      <Button
                        variant="contained"
                        startIcon={<ShopIcon />}
                        href={selectedApp.play_store_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ bgcolor: '#01875f', borderRadius: 3 }}
                      >
                        {t('showcase.googlePlay')}
                      </Button>
                    )}
                    {selectedApp.app_store_url && (
                      <Button
                        variant="contained"
                        startIcon={<AppleIcon />}
                        href={selectedApp.app_store_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ bgcolor: '#000', borderRadius: 3 }}
                      >
                        {t('showcase.appStore')}
                      </Button>
                    )}
                    {selectedApp.website_url && (
                      <Button
                        variant="outlined"
                        startIcon={<LanguageIcon />}
                        href={selectedApp.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ borderRadius: 3 }}
                      >
                        {t('showcase.visitWebsite')}
                      </Button>
                    )}
                  </Stack>
                </DialogContent>
              </motion.div>
            </AnimatePresence>
            );
          })()}
        </Dialog>
      </Box>
    </PageTransition>
  );
}
